import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, IconButton, TextField,
  CircularProgress, Chip, Grid, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, Tooltip, FormControl, InputLabel,
  Select, MenuItem, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import {
  Refresh, Download, Search, Battery3Bar, BarChart,
} from '@mui/icons-material';
import { InvokeApi, apiList } from '../services/ApiService';
import { Config } from '../config/Config';

/**
 * Fleet Tracking Report - View all vehicles with monthly/daily statistics
 * Shows: 1000+ vehicles with distance traveled, journeys, statistics by month/day
 * Optimized for large datasets with pagination and filtering
 */
const FleetTrackingReport = () => {
  const [vehicles, setVehicles] = useState([]);
  const [filteredVehicles, setFilteredVehicles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [reportType, setReportType] = useState('monthly'); // monthly or daily
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [vehicleStats, setVehicleStats] = useState({});
  const [loadingStats, setLoadingStats] = useState(false);
  const [expandedVehicles, setExpandedVehicles] = useState(new Set());

  // Fetch all vehicles
  const fetchVehicles = useCallback(async () => {
    setLoading(true);
    try {
      const res = await InvokeApi(Config.base_url + '/admin/vehicles', {
        page: 1,
        per_page: 1000,
        search: '',
        status: '',
      });
      if (res?.code === '200') {
        setVehicles(res.vehicles || []);
      }
    } catch (err) {
      console.error('Failed to fetch vehicles:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch statistics for a single vehicle — same endpoints as VehicleDetail + FleetMovementReport
  const fetchVehicleStats = useCallback(async (vehicleId) => {
    try {
      if (reportType === 'monthly') {
        const [yr, mo] = selectedMonth.split('-');
        const res = await InvokeApi(Config.base_url + apiList.getVehicleSummary, {
          vehicle_id: vehicleId,
          month: parseInt(mo),
          year:  parseInt(yr),
        });
        if (res?.code === '200' && res.summary) {
          // Normalise to a flat shape the table expects
          setVehicleStats(prev => ({
            ...prev,
            [vehicleId]: {
              total_distance_km:     res.summary.total_km,
              days_active:           res.summary.active_days,
              avg_daily_distance_km: res.summary.active_days > 0
                ? Math.round((res.summary.total_km / res.summary.active_days) * 100) / 100
                : 0,
              journey_count:         res.summary.active_days,
              daily_breakdown:       res.summary.daily_breakdown, // [{date, distance_km}]
            },
          }));
        }
      } else {
        const res = await InvokeApi(Config.base_url + apiList.getVehiclePathHistory, {
          vehicle_id: vehicleId,
          date: selectedDate,
        });
        if (res?.code === '200') {
          // Compute total distance from sampled points using Haversine in JS
          let totalKm = 0;
          const pts = res.points || [];
          for (let i = 1; i < pts.length; i++) {
            const R = 6371;
            const dLat = (pts[i].latitude  - pts[i-1].latitude)  * Math.PI / 180;
            const dLon = (pts[i].longitude - pts[i-1].longitude) * Math.PI / 180;
            const a = Math.sin(dLat/2)**2 + Math.cos(pts[i-1].latitude * Math.PI/180)
                    * Math.cos(pts[i].latitude * Math.PI/180) * Math.sin(dLon/2)**2;
            const km = R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
            if (km < 2) totalKm += km;
          }
          setVehicleStats(prev => ({
            ...prev,
            [vehicleId]: {
              total_distance_km:     Math.round(totalKm * 100) / 100,
              days_active:           pts.length > 0 ? 1 : 0,
              avg_daily_distance_km: Math.round(totalKm * 100) / 100,
              journey_count:         pts.length > 0 ? 1 : 0,
              daily_breakdown:       [],
            },
          }));
        }
      }
    } catch (err) {
      console.error(`Failed to fetch stats for vehicle ${vehicleId}:`, err);
    }
  }, [reportType, selectedMonth, selectedDate]);

  // Initial load
  useEffect(() => {
    fetchVehicles();
  }, [fetchVehicles]);

  // Apply filters
  useEffect(() => {
    let filtered = vehicles;

    if (search) {
      const q = search.toLowerCase();
      filtered = filtered.filter(
        v => v.vehicle_number?.toLowerCase().includes(q) ||
             v.model?.toLowerCase().includes(q) ||
             v.chassis_number?.toLowerCase().includes(q)
      );
    }

    if (statusFilter) {
      filtered = filtered.filter(v => v.status === statusFilter);
    }

    setFilteredVehicles(filtered);
    setPage(0); // Reset to first page on filter change
  }, [vehicles, search, statusFilter]);

  // Handle pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangePageSize = (event) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Toggle vehicle expansion and load stats
  const toggleVehicleExpand = (vehicleId) => {
    const newExpanded = new Set(expandedVehicles);
    if (newExpanded.has(vehicleId)) {
      newExpanded.delete(vehicleId);
    } else {
      newExpanded.add(vehicleId);
      // Fetch stats if not already loaded
      if (!vehicleStats[vehicleId]) {
        fetchVehicleStats(vehicleId);
      }
    }
    setExpandedVehicles(newExpanded);
  };

  // Get paginated data
  const paginatedVehicles = filteredVehicles.slice(
    page * pageSize,
    page * pageSize + pageSize
  );

  const getStatusColor = (status) => {
    const colors = {
      active: 'success',
      maintenance: 'warning',
      assigned: 'info',
      retired: 'error',
    };
    return colors[status] || 'default';
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Box>
            <Typography variant="h5" fontWeight={800} color="#1a237e">
              <BarChart sx={{ mr: 1, verticalAlign: 'middle' }} />
              Fleet Tracking Report
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Monthly and daily statistics for all {vehicles.length}+ vehicles
            </Typography>
          </Box>
          <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
            <Tooltip title="Refresh Data">
              <IconButton onClick={fetchVehicles} disabled={loading} sx={{ color: '#1a237e' }}>
                <Refresh />
              </IconButton>
            </Tooltip>
            <Tooltip title="Download Report">
              <IconButton sx={{ color: '#1a237e' }}>
                <Download />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Report Type Toggle */}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2, alignItems: 'center' }}>
          <ToggleButtonGroup
            value={reportType}
            exclusive
            onChange={(e, newType) => newType && setReportType(newType)}
            size="small"
          >
            <ToggleButton value="monthly" sx={{ color: '#1a237e', '&.Mui-selected': { backgroundColor: '#1a237e', color: '#fff' } }}>
              📅 Monthly
            </ToggleButton>
            <ToggleButton value="daily" sx={{ color: '#1a237e', '&.Mui-selected': { backgroundColor: '#1a237e', color: '#fff' } }}>
              📊 Daily
            </ToggleButton>
          </ToggleButtonGroup>

          {reportType === 'monthly' && (
            <TextField
              type="month"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              size="small"
              sx={{ width: 150 }}
            />
          )}

          {reportType === 'daily' && (
            <TextField
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              size="small"
              sx={{ width: 150 }}
            />
          )}
        </Box>

        {/* Filters */}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            placeholder="Search vehicle..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
            InputProps={{ startAdornment: <Search sx={{ mr: 1, color: '#999' }} /> }}
            sx={{ width: 250 }}
          />

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="">All Status</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="assigned">Assigned</MenuItem>
              <MenuItem value="maintenance">Maintenance</MenuItem>
              <MenuItem value="retired">Retired</MenuItem>
            </Select>
          </FormControl>

          <Box sx={{ ml: 'auto' }}>
            <Chip
              label={`Showing ${paginatedVehicles.length} of ${filteredVehicles.length}`}
              color="primary"
              variant="outlined"
            />
          </Box>
        </Box>
      </Box>

      {/* Main Table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress sx={{ color: '#1a237e' }} />
        </Box>
      ) : (
        <Card sx={{ borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ background: '#f5f6fa' }}>
                  <TableCell sx={{ fontWeight: 700, fontSize: 12, color: '#1a237e', width: 40 }}>Expand</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 12, color: '#1a237e' }}>Vehicle</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 12, color: '#1a237e' }}>Model</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 12, color: '#1a237e', textAlign: 'center' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 12, color: '#1a237e', textAlign: 'center' }}>Battery</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 12, color: '#1a237e', textAlign: 'right' }}>Distance</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 12, color: '#1a237e', textAlign: 'right' }}>Journeys</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 12, color: '#1a237e', textAlign: 'right' }}>Avg Speed</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedVehicles.map((vehicle) => {
                  const stats = vehicleStats[vehicle.id];
                  const isExpanded = expandedVehicles.has(vehicle.id);
                  const batteryColor = vehicle.battery_pct >= 60 ? '#16a34a' : vehicle.battery_pct >= 30 ? '#d97706' : '#dc2626';

                  return (
                    <React.Fragment key={vehicle.id}>
                      {/* Main Row */}
                      <TableRow hover sx={{ cursor: 'pointer', '&:hover': { background: '#f9fafb' } }}>
                        <TableCell sx={{ textAlign: 'center' }}>
                          <IconButton
                            size="small"
                            onClick={() => toggleVehicleExpand(vehicle.id)}
                            sx={{ color: '#1a237e' }}
                          >
                            {isExpanded ? '▼' : '▶'}
                          </IconButton>
                        </TableCell>
                        <TableCell>
                          <Typography fontWeight={600} sx={{ fontSize: 12 }}>
                            {vehicle.vehicle_number}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                            {vehicle.chassis_number}
                          </Typography>
                        </TableCell>
                        <TableCell fontSize={12}>{vehicle.model}</TableCell>
                        <TableCell sx={{ textAlign: 'center' }}>
                          <Chip
                            label={vehicle.status}
                            size="small"
                            color={getStatusColor(vehicle.status)}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell sx={{ textAlign: 'center' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                            <Battery3Bar sx={{ fontSize: 16, color: batteryColor }} />
                            <Typography fontWeight={600} sx={{ color: batteryColor, fontSize: 12 }}>
                              {vehicle.battery_pct}%
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ textAlign: 'right', fontWeight: 600, fontSize: 12 }}>
                          {stats ? `${stats.total_distance_km} km` : '—'}
                        </TableCell>
                        <TableCell sx={{ textAlign: 'right', fontWeight: 600, fontSize: 12 }}>
                          {stats ? stats.journey_count || stats.daily_stats?.length || 0 : '—'}
                        </TableCell>
                        <TableCell sx={{ textAlign: 'right', fontSize: 12 }}>
                          {stats ? `${stats.avg_daily_distance_km ? Math.round(stats.total_distance_km / Math.max(1, stats.journey_count || 1)) : 0} km/h` : '—'}
                        </TableCell>
                      </TableRow>

                      {/* Expanded Row */}
                      {isExpanded && (
                        <TableRow sx={{ background: '#f9fafb' }}>
                          <TableCell colSpan={8} sx={{ p: 2 }}>
                            {stats ? (
                              <Grid container spacing={2}>
                                {/* Summary Cards */}
                                <Grid item xs={12}>
                                  <Grid container spacing={1}>
                                    <Grid item xs={3} sm={2}>
                                      <Card sx={{ borderRadius: 1, background: 'linear-gradient(135deg,#1a237e20,#1a237e10)', border: '1px solid #1a237e' }}>
                                        <CardContent sx={{ py: 1, px: 1.5 }}>
                                          <Typography variant="caption" sx={{ fontSize: 10, fontWeight: 600, color: 'text.secondary' }}>Distance</Typography>
                                          <Typography variant="body2" fontWeight={700} color="#1a237e">{stats.total_distance_km} km</Typography>
                                        </CardContent>
                                      </Card>
                                    </Grid>
                                    <Grid item xs={3} sm={2}>
                                      <Card sx={{ borderRadius: 1, background: 'linear-gradient(135deg,#ff980020,#ff980010)', border: '1px solid #ff9800' }}>
                                        <CardContent sx={{ py: 1, px: 1.5 }}>
                                          <Typography variant="caption" sx={{ fontSize: 10, fontWeight: 600, color: 'text.secondary' }}>Days Active</Typography>
                                          <Typography variant="body2" fontWeight={700} color="#ff9800">{stats.days_active || stats.journey_count || 0}</Typography>
                                        </CardContent>
                                      </Card>
                                    </Grid>
                                    <Grid item xs={3} sm={2}>
                                      <Card sx={{ borderRadius: 1, background: 'linear-gradient(135deg,#43a04720,#43a04710)', border: '1px solid #43a047' }}>
                                        <CardContent sx={{ py: 1, px: 1.5 }}>
                                          <Typography variant="caption" sx={{ fontSize: 10, fontWeight: 600, color: 'text.secondary' }}>Avg Daily</Typography>
                                          <Typography variant="body2" fontWeight={700} color="#43a047">{stats.avg_daily_distance_km} km</Typography>
                                        </CardContent>
                                      </Card>
                                    </Grid>
                                  </Grid>
                                </Grid>

                                {/* Daily Breakdown Table */}
                                {stats.daily_breakdown && stats.daily_breakdown.length > 0 && (
                                  <Grid item xs={12}>
                                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, color: '#1a237e', fontSize: 12 }}>
                                      Daily Breakdown
                                    </Typography>
                                    <TableContainer sx={{ borderRadius: 1, border: '1px solid #e2e8f0' }}>
                                      <Table size="small">
                                        <TableHead>
                                          <TableRow sx={{ background: '#f5f6fa' }}>
                                            <TableCell sx={{ fontWeight: 600, fontSize: 11, color: '#1a237e' }}>Date</TableCell>
                                            <TableCell sx={{ fontWeight: 600, fontSize: 11, color: '#1a237e', textAlign: 'right' }}>Distance</TableCell>
                                          </TableRow>
                                        </TableHead>
                                        <TableBody>
                                          {stats.daily_breakdown.map((day, i) => (
                                            <TableRow key={i} sx={{ '&:hover': { background: '#f9fafb' } }}>
                                              <TableCell sx={{ fontSize: 11 }}>{day.date}</TableCell>
                                              <TableCell sx={{ textAlign: 'right', fontSize: 11, fontWeight: 600 }}>{day.distance_km} km</TableCell>
                                            </TableRow>
                                          ))}
                                        </TableBody>
                                      </Table>
                                    </TableContainer>
                                  </Grid>
                                )}
                              </Grid>
                            ) : (
                              <Box sx={{ textAlign: 'center', py: 2 }}>
                                <CircularProgress size={24} sx={{ color: '#1a237e' }} />
                              </Box>
                            )}
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={filteredVehicles.length}
            rowsPerPage={pageSize}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangePageSize}
          />
        </Card>
      )}
    </Box>
  );
};

export default FleetTrackingReport;
